import React, { useEffect, useRef } from 'react';
import { Alert, Button, Form, Icon, Input } from 'antd';
import InfoBox from '../../../components/InfoBox/InfoBox';
import { ORG_ONBOARDING_INFO_COLOR } from '../onboardingConstants';

const FormItem = Form.Item;

const OTP_RULES = [
  { required: true, message: 'Enter the verification code from your email.' },
  { pattern: /^\d{6}$/, message: 'Enter the 6-digit code from your email.' },
];

export default function OnboardingOtpVerification({
  form,
  email,
  otpError,
  isVerifying,
  isResending,
  onVerify,
  onResend,
}) {
  const codeInputRef = useRef(null);
  const { getFieldDecorator } = form;

  useEffect(() => {
    if (codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, []);

  const handleVerify = () => {
    form.validateFields(['otpCode'], (err, values) => {
      if (err) return;
      onVerify(values.otpCode);
    });
  };

  return (
    <section
      className="onboarding-registration-pre-gate onboarding-registration-otp"
      aria-labelledby="onboarding-registration-otp-heading"
    >
      <div className="onboarding-registration-pre-gate-header">
        <span className="onboarding-registration-panel-icon onboarding-registration-pre-gate-icon onboarding-registration-otp-icon">
          <Icon type="mail" />
        </span>
        <div className="onboarding-registration-pre-gate-heading" id="onboarding-registration-otp-heading">
          <strong>Verify your email</strong>
          <small>One last step — enter the code we emailed you to activate your account.</small>
        </div>
      </div>

      <div className="onboarding-registration-pre-gate-body">
        <div className="onboarding-registration-pre-gate-main">
          <InfoBox
            className="onboarding-registration-link-details"
            color={ORG_ONBOARDING_INFO_COLOR}
            honorSectionGuidanceToggle={false}
            message={
              <div>
                <p>
                  <strong>Check your inbox</strong>
                </p>
                <p>
                  We sent a one-time verification code to <strong>{email}</strong>. It may take a minute to arrive —
                  check your spam folder if you do not see it.
                </p>
              </div>
            }
          />

          {otpError && (
            <Alert
              className="onboarding-registration-link-details"
              showIcon
              type="error"
              message="Verification failed"
              description={otpError}
            />
          )}

          <FormItem label="Verification code">
            {getFieldDecorator('otpCode', {
              rules: OTP_RULES,
              getValueFromEvent: (event) => event.target.value.replace(/\D/g, '').slice(0, 6),
            })(
              <Input
                size="large"
                prefix={<Icon type="lock" />}
                placeholder="000000"
                disabled={isVerifying || isResending}
                autoComplete="one-time-code"
                inputMode="numeric"
                name="one-time-code"
                ref={codeInputRef}
                maxLength={6}
                className="onboarding-registration-otp-input"
                aria-label="6-digit email verification code"
                onPressEnter={handleVerify}
              />,
            )}
          </FormItem>
          <p className="onboarding-registration-field-help">
            Enter the 6-digit code from your email. Codes expire after a short time.
          </p>
        </div>

        <div className="onboarding-registration-actions onboarding-registration-actions-between onboarding-registration-actions-bar">
          <Button
            type="link"
            htmlType="button"
            className="onboarding-registration-otp-resend"
            disabled={isVerifying || isResending}
            loading={isResending}
            onClick={onResend}
          >
            <Icon type="reload" /> Resend code
          </Button>
          <Button
            type="primary"
            htmlType="button"
            loading={isVerifying}
            disabled={isResending}
            onClick={handleVerify}
          >
            <Icon type="check-circle" /> Verify &amp; continue
          </Button>
        </div>
      </div>
    </section>
  );
}
